import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';
import { Grid } from '@mui/material';
import { SelectChangeEvent } from '@mui/material';
import { Icons } from 'greener-ui-library';
import { MultiSelectInputProps, Option } from './MultiSelectInput.types';

const defaultRenderOption = (option: Option, selectedOptions: (number | string)[]) => (
  <MenuItem key={option.id} value={option.id} disabled={option.disabled}>
    <Checkbox checked={selectedOptions.includes(option.id)} />
    <ListItemText primary={option.title} />
  </MenuItem>
);

const defaultRenderValue = (id: number | string, options: Option[]) => {
  const option = options.find((option) => option.id === id);
  return option ? option.title : '';
};

export const MultiSelectInput = ({
  title,
  options = [],
  selectedOptions = [],
  renderOption = defaultRenderOption,
  renderValue = defaultRenderValue,
  onChange,
  onRemoveAll,
  sx,
  isError = false,
}: MultiSelectInputProps) => {
  const handleChange = (event: SelectChangeEvent<(number | string)[]>) => {
    onChange(event.target.value as (number | string)[]);
  };

  const hasClearable = onRemoveAll && selectedOptions.some((id) => !options.find((o) => o.id === id)?.disabled);

  return (
    <Grid item>
      <FormControl
        error={isError}
        sx={{
          ...sx,
          position: 'relative',
          '& .MultiSelectInput-clearBtn': { opacity: 0 },
          '&:hover .MultiSelectInput-clearBtn': { opacity: 1 },
        }}
      >
        <InputLabel
          id="multi-select-label"
          sx={{
            '&.MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': {
              transform: (theme) => `translate(${theme.spacing(2)}, ${theme.spacing(1)}) `,
            },
          }}
        >
          {title}
        </InputLabel>
        {hasClearable && (
          <IconButton
            className="MultiSelectInput-clearBtn"
            size="small"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRemoveAll?.();
            }}
            sx={{
              position: 'absolute',
              right: 28,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1,
              transition: 'opacity 0.15s',
            }}
          >
            <Icons.Close fontSize="small" />
          </IconButton>
        )}
        <Select
          labelId="multi-select-label"
          id="multi-select"
          multiple
          size="small"
          value={selectedOptions}
          onChange={handleChange}
          input={<OutlinedInput label={title} />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(selected as (number | string)[]).map((id) => {
                const option = options.find((o) => o.id === id);
                const isDeletable = !option?.disabled;
                return (
                  <Chip
                    key={id}
                    size="small"
                    label={renderValue(id, options)}
                    onMouseDown={(e) => isDeletable && e.stopPropagation()}
                    onDelete={
                      isDeletable
                        ? (e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            onChange((selected as (number | string)[]).filter((s) => s !== id));
                          }
                        : undefined
                    }
                  />
                );
              })}
            </Box>
          )}
          MenuProps={{
            PaperProps: {
              style: {
                minWidth: 250,
              },
            },
          }}
        >
          {options.map((option) => renderOption(option, selectedOptions))}
        </Select>
      </FormControl>
    </Grid>
  );
};
